import ReactGA from 'react-ga4';
import { numify } from 'numify';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
/////////////////////////////////////////////////////////////
import { telegramWebApp } from '@/main';
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { HackService } from '@/core/services';
import {
	setCounter,
	setDiamonds,
	setEarnPerTap,
	setMultiTapCostLevelUp,
	setMultiTapLevel,
	setStickerWonDialog,
	setTotalCoinsGenerated,
} from '@/core/redux/slices';
import { UpgradeMultitapResponse } from '@/core/interfaces';
import {
	Drawer,
	DrawerContent,
	DrawerClose,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Button,
	Close,
	CoinImage,
	DrawerDescription,
} from '@/app/shared/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface MultiTapProps {
	isOpen: boolean;
	onClose: React.Dispatch<React.SetStateAction<boolean>>;
	image: string;
}

export const MultiTap: React.FC<MultiTapProps> = ({
	isOpen,
	onClose,
	image,
}: MultiTapProps) => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();

	/*********************
	// #region Redux
	**********************/
	const { counter } = useCustomSelector((state) => state.counter);
	const { multiTapCostLevelUp, multiTapLevel } = useCustomSelector(
		(state) => state.hacks
	);

	/*********************
	// #region Functions
	**********************/
	const handleRequestUpgradeMultiTap = async () => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');
		if (counter < multiTapCostLevelUp) {
			toast.warning(t('general.notEnough'));
			return;
		}

		toast.promise(HackService.upgradeMultiTap(), {
			loading: t('general.loading'),
			success: (data: UpgradeMultitapResponse) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'boosts',
					action: 'Upgrade multitap',
				});

				dispatch(setMultiTapLevel(data.data.level));
				dispatch(setMultiTapCostLevelUp(data.data.nextPrice));
				dispatch(setCounter(data.currentCoins));
				dispatch(setDiamonds(data.currentDiamonds));
				dispatch(setEarnPerTap(data.data.earnPerTap));
				dispatch(setTotalCoinsGenerated(data.totalCoinsEarned));

				// Check if has a gift
				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				return `${t('hacks.multiTap.upgradeMultitap')} ${
					data.data.level
				}.`;
			},
			error: t('hacks.multiTap.upgradeMultitapError'),
		});
		onClose(false);
	};

	/*********************
	// #region Template
	**********************/
	return (
		<Drawer open={isOpen} onOpenChange={onClose}>
			<DrawerContent>
				<div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
				<div className="mx-auto w-full max-w-sm ">
					<DrawerHeader>
						<DrawerTitle>
							<VisuallyHidden>
								{t('hacks.multiTap.title')}
							</VisuallyHidden>
							<DrawerClose className="right-5 text-right absolute">
								<Close fillcolor="#fff" />
							</DrawerClose>
						</DrawerTitle>
					</DrawerHeader>
					<DrawerDescription>
						<VisuallyHidden>
							{t('hacks.multiTap.description')}{' '}
							{multiTapLevel + 1}
						</VisuallyHidden>
					</DrawerDescription>

					<div className="mx-auto px-8">
						<img
							className="cursor-pointer mx-auto w-[100px] mb-6"
							src={image}
						/>

						<div className="flex flex-col text-center gap-3 mb-8">
							<p className="font-bold text-2xl">
								{t('hacks.multiTap.title')}
							</p>
							<p className="font-semibold text-sm text-white text-center">
								{t('hacks.multiTap.description')}{' '}
								{multiTapLevel + 1}
							</p>
						</div>

						<div className="flex my-4 justify-center">
							<CoinImage />
							<p className="text-lg font-bold">
								{numify(multiTapCostLevelUp)}{' '}
								{t('general.toLevel')} {multiTapLevel + 1}
							</p>
						</div>
					</div>
					<DrawerFooter>
						<DrawerClose asChild>
							<Button
								variant="primary"
								className="py-6 w-full text-sm rounded-lg"
								onClick={handleRequestUpgradeMultiTap}
							>
								{t('general.upgrade')}
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
};
